import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { adminAuditPaths } from '@app/modules/audit/routing/admin-audit-paths';
import { useAuditReportsUpdateReportDefinition } from '@mi-front/api-v2';
import { Save } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReportDefinition } from '../../../types';
import { useDefinitionContext } from '../../contexts/DefinitionContext';

export function SaveButton() {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const { initialDefinition } = useDefinitionContext();
  const { reset, formState, getValues } = useFormContext<ReportDefinition>();
  const queryClient = useQueryClient();

  const updateMutation = useAuditReportsUpdateReportDefinition({
    mutation: {
      onSuccess: (result) => {
        notifySuccess(
          $t({
            id: 'audit.reportDefinitions.saveSuccess',
            defaultMessage: 'Plantilla de reporte guardada exitosamente',
          }),
        );

        // Reset form with saved data to clear dirty state
        if (result) {
          reset({
            name: result.name,
            isDefault: result.isDefault,
            data: result.definition,
          });
        }

        // Invalidate all report definition queries since isDefault changes can affect other definitions
        queryClient
          .invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              const queryMetadata = queryKey[1];

              return (
                Array.isArray(queryKey) &&
                queryKey[0] === 'api-v2' &&
                typeof queryMetadata === 'object' &&
                queryMetadata !== null &&
                'url' in queryMetadata &&
                typeof queryMetadata.url === 'string' &&
                queryMetadata.url.includes('/audit-reports/v1/report-definitions')
              );
            },
          })
          .then(() => {
            setImmediate(() => {
              pushTo(adminAuditPaths.reportDefinitions(initialDefinition.reportType));
            });
          });
      },
      onError: (error) => {
        notifyError(
          $t({
            id: 'audit.reportDefinitions.saveError',
            defaultMessage: 'Error al guardar la plantilla de reporte',
          }),
        );
        console.error(error);
      },
    },
  });

  const handleSave = () => {
    const values = getValues();
    updateMutation.mutate({
      id: initialDefinition.id,
      data: {
        definition: values.data,
        name: values.name,
        isDefault: values.isDefault,
      },
    });
  };

  return (
    <Button
      variant="contained"
      size="large"
      onClick={handleSave}
      loading={updateMutation.isPending}
      disabled={!formState.isDirty}
      startIcon={<Save />}
    >
      <FormattedMessage id="common.form.saveBtnText" defaultMessage="Guardar" />
    </Button>
  );
}
